import { useEffect, useState } from 'react';
import {
  ActionType,
  IntegrationTermsAndConditions,
  IntergrationInstructionStep,
  StrVal
} from '../../../lib/api/account-linking/types';

import Step, { StepRow } from './Step';
import { IsIntegrationValid } from '../lib/helpers/ValidateIntegration';
import { getDefaultInputValue } from '../lib/helpers/StepInputOptions';

import TermsLabel from './TermsLabel';
import { P2GGrid, P2GFade, P2GBox, P2GButton, P2GText } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import React from 'react';

interface props {
  action: ActionType;
  integrationSlug: string;
  integrationType: string;
  steps: IntergrationInstructionStep[];
  hasInputs: boolean;
  showInstructions: boolean;
  saving: boolean;
  readonly: boolean;
  onSubmit: (values: StrVal) => void;
  terms: IntegrationTermsAndConditions;
}

const Steps: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const [values, setValues] = useState<StrVal>({});
  const [isValid, setIsValid] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initialValues: StrVal = {};
    props.steps?.forEach((s) => {
      s?.inputs.forEach((i) => {
        if (i.type === 'ExternalFile' || initialValues[i.propertyName]) {
          return;
        }
        const seed = i.value || getDefaultInputValue(i);
        if (seed) {
          initialValues[i.propertyName] = seed;
        }
      });
    });

    setIsValid(IsIntegrationValid(initialValues, props.steps, props.terms));
    setValues(initialValues);
    setLoading(false);
  }, [props.steps, props.action, props.integrationSlug, props.terms]);

  // Keep validity in sync with the current values (functional updates above mean
  // we no longer recompute it inline against a stale snapshot).
  useEffect(() => {
    setIsValid(IsIntegrationValid(values, props.steps, props.terms));
  }, [values, props.steps, props.terms]);

  const onUpdate = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const showNumber = props.showInstructions && props.steps.length > 1;

  if (loading) {
    return null;
  }

  return (
    <P2GGrid container spacing={1} direction="column" key={`${props.showInstructions}`}>
      {props.steps.map((s, index) => (
        <React.Fragment key={`${index}`}>
          {s.title && (
            <P2GGrid item>
              <P2GBox my={1}>
                <P2GText variant="subtitle2">{t(s.title.translationKey, { defaultValue: s.title.content })}</P2GText>
              </P2GBox>
            </P2GGrid>
          )}
          <P2GGrid item>
            <P2GFade in timeout={(index + 1) * 550}>
              <Step
                saving={props.saving}
                step={s}
                showInstructions={props.showInstructions}
                showNumber={showNumber}
                stepNumber={index + 1}
                values={values}
                onUpdate={onUpdate}
                integrationType={props.integrationType}
                integrationSlug={props.integrationSlug}
                action={props.action}
              />
            </P2GFade>
            {!props.readonly && index == props.steps.length - 1 && (
              <StepRow
                left={!!showNumber}
                right={
                  <>
                    {props.terms && (
                      <P2GBox mt={props.hasInputs ? 2 : 0}>
                        <TermsLabel
                          disabled={props.saving}
                          terms={props.terms}
                          value={values[props.terms.input.propertyName]}
                          onUpdate={onUpdate}
                          action={props.action}
                          integrationSlug={props.integrationSlug}
                          integrationType={props.integrationType}
                        />
                      </P2GBox>
                    )}
                    <P2GBox mt={props.hasInputs ? 2 : 0}>
                      <P2GButton
                        disabled={!isValid || props.saving}
                        loading={props.saving}
                        onClick={() => props.onSubmit(values)}
                        variant="primary"
                        fullWidth
                        size="large"
                      >
                        <Trans
                          t={t}
                          i18nKey={`${props.integrationSlug}_${props.action}_SUBMIT_BUTTON`.toUpperCase()}
                          defaults="{{action}} Account"
                          values={{ action: props.action }}
                        />
                      </P2GButton>
                    </P2GBox>
                  </>
                }
              />
            )}
          </P2GGrid>
        </React.Fragment>
      ))}
    </P2GGrid>
  );
};

export default Steps;
